import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ReactComponent as ChevronLeftIcon } from "feather-icons/dist/icons/chevron-left.svg";
import { ReactComponent as ChevronRightIcon } from "feather-icons/dist/icons/chevron-right.svg";
import { ReactComponent as ClockIcon } from "feather-icons/dist/icons/clock.svg";
import { ReactComponent as ArrowRightIcon } from "feather-icons/dist/icons/arrow-right.svg";

import CoverArt from "./CoverArt";
import { VisuallyHidden } from "./ui";
import { getLatestPosts } from "../data/posts";
import { formatDateShort, readingTimeLabel } from "../utils/text";

/**
 * Carrousel des derniers articles (hero de la page d'accueil).
 * - Défilement automatique (pause au survol, au focus clavier et quand l'onglet est masqué),
 *   flèches, points, balayage tactile.
 * - Chaque diapositive est un lien vers l'article : photo teintée (CoverArt), catégorie, titre, date.
 * - Respecte prefers-reduced-motion (pas de défilement automatique, transitions instantanées).
 */

const Wrapper = tw.div`relative w-full`;
const FrameWrap = tw.div`relative`;
const Frame = styled.div`
  ${tw`relative z-20 w-full rounded-4xl overflow-hidden shadow-raised bg-primary-900`}
  aspect-ratio: 16 / 10;
`;

const Slide = styled(motion(Link))`
  ${tw`absolute inset-0 block focus:outline-none`}
  &:focus-visible {
    box-shadow: inset 0 0 0 4px rgba(255, 255, 255, 0.9);
  }
`;
const Overlay = styled.div`
  ${tw`absolute inset-0 pointer-events-none`}
  background: linear-gradient(to top, rgba(26, 32, 44, 0.92) 0%, rgba(26, 32, 44, 0.6) 38%, rgba(26, 32, 44, 0) 68%);
`;
const Text = tw.div`absolute inset-x-0 bottom-0 p-6 sm:p-8 text-gray-100`;
const Badge = styled.span(({ $color }) => [
  tw`inline-block uppercase text-xs font-bold tracking-widest px-3 py-1 rounded-full text-gray-100`,
  `background-color: ${$color};`,
]);
const Title = styled.h3`
  ${tw`mt-3 text-lg sm:text-xl lg:text-2xl font-black leading-tight text-gray-100`}
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
const Meta = styled.p`
  ${tw`mt-2 text-xs uppercase font-semibold tracking-wide text-gray-400 flex items-center flex-wrap`}
  svg {
    ${tw`w-4 h-4 mr-1`}
  }
`;
const ReadMore = styled.span`
  ${tw`mt-3 inline-flex items-center text-sm font-bold text-gray-100`}
  svg {
    ${tw`w-4 h-4 ml-2 transition duration-300 transform`}
  }
  .group:hover & svg {
    ${tw`translate-x-1`}
  }
`;

const Controls = tw.div`mt-4 flex items-center justify-between`;
const Dots = tw.div`flex items-center`;
const Dot = styled.button(({ $active }) => [
  tw`h-2 rounded-full bg-primary-500 mr-2 last:mr-0 transition-all duration-300 focus:outline-none focus:shadow-outline`,
  $active ? tw`w-6 opacity-100` : tw`w-2 opacity-25 hover:opacity-50`,
]);
const Arrows = tw.div`flex items-center`;
const Arrow = styled.button`
  ${tw`w-10 h-10 ml-2 rounded-full bg-gray-100 text-primary-500 flex items-center justify-center shadow transition duration-300`}
  ${tw`hocus:bg-primary-500 hocus:text-gray-100 focus:outline-none focus:shadow-outline`}
  svg {
    ${tw`w-5 h-5`}
  }
`;

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);
  return reduced;
};

const variants = {
  enter: direction => ({ x: direction > 0 ? 64 : -64, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: direction => ({ x: direction > 0 ? -64 : 64, opacity: 0 }),
};

/**
 * @param {number} count           nombre d'articles (les plus récents)
 * @param {number} interval        délai entre deux diapositives (ms)
 * @param {string} label           libellé accessible du carrousel
 * @param {ReactNode} decorBehind  décor rendu DERRIÈRE le cadre (ex. rectangle décalé)
 * @param {ReactNode} decorFront   décor rendu autour du cadre, au-dessus de decorBehind (ex. motif de points)
 */
export default function PostCarousel({ count = 5, interval = 5000, label = "Derniers articles", decorBehind = null, decorFront = null }) {
  const posts = useMemo(() => getLatestPosts(count), [count]);
  const total = posts.length;
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [hovered, setHovered] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const reduced = usePrefersReducedMotion();
  const touchStartX = useRef(null);

  const go = useCallback(
    (dir, manual = false) => {
      if (total < 2) return;
      setDirection(dir);
      setIndex(current => {
        const next = (current + dir + total) % total;
        if (manual) setAnnouncement(`Article ${next + 1} sur ${total} : ${posts[next].title}`);
        return next;
      });
    },
    [total, posts]
  );

  const goTo = useCallback(
    target => {
      if (target === index) return;
      setDirection(target > index ? 1 : -1);
      setIndex(target);
      setAnnouncement(`Article ${target + 1} sur ${total} : ${posts[target].title}`);
    },
    [index, total, posts]
  );

  // Défilement automatique, relancé à chaque changement (manuel ou non).
  useEffect(() => {
    if (hovered || hidden || reduced || total < 2) return undefined;
    const id = setInterval(() => go(1), interval);
    return () => clearInterval(id);
  }, [hovered, hidden, reduced, total, interval, go, index]);

  // Pause quand l'onglet n'est pas visible.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  if (!total) return null;
  const post = posts[index];
  const transition = reduced ? { duration: 0 } : { type: "spring", stiffness: 260, damping: 30 };

  const onTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = e => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1, true);
  };

  return (
    <Wrapper
      role="region"
      aria-roledescription="carrousel"
      aria-label={label}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
    >
      <FrameWrap>
        {decorBehind}
        <Frame onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <AnimatePresence initial={false} custom={direction}>
            <Slide
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group"
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              aria-label={`${post.title} — lire l'article`}
            >
              <CoverArt seed={post.slug} color={post.category.color} image={post.cover.image} plain={post.cover.plain} alt="" eager={index === 0} />
              <Overlay aria-hidden="true" />
              <Text>
                <Badge $color={post.category.color}>{post.category.label}</Badge>
                <Title>{post.title}</Title>
                <Meta>
                  <time dateTime={post.publishedAt}>{formatDateShort(post.publishedAt)}</time>
                  <span aria-hidden="true">&nbsp;·&nbsp;</span>
                  <ClockIcon aria-hidden="true" />
                  {readingTimeLabel(post.readingTime)}
                </Meta>
                <ReadMore>
                  Lire l'article <ArrowRightIcon aria-hidden="true" />
                </ReadMore>
              </Text>
            </Slide>
          </AnimatePresence>
        </Frame>
        {decorFront}
      </FrameWrap>

      {total > 1 && (
        <Controls>
          <Dots role="tablist" aria-label="Choisir un article">
            {posts.map((p, i) => (
              <Dot
                key={p.slug}
                type="button"
                role="tab"
                aria-selected={i === index}
                aria-label={`Article ${i + 1} sur ${total} : ${p.title}`}
                $active={i === index}
                onClick={() => goTo(i)}
              />
            ))}
          </Dots>
          <Arrows>
            <Arrow type="button" onClick={() => go(-1, true)} aria-label="Article précédent">
              <ChevronLeftIcon aria-hidden="true" />
            </Arrow>
            <Arrow type="button" onClick={() => go(1, true)} aria-label="Article suivant">
              <ChevronRightIcon aria-hidden="true" />
            </Arrow>
          </Arrows>
        </Controls>
      )}
      <VisuallyHidden aria-live="polite" aria-atomic="true">
        {announcement}
      </VisuallyHidden>
    </Wrapper>
  );
}
